import { useState } from 'react'
import { HttpVerb } from '../../../../../../../types/api-types'
import { ExternalDocumentReferencesEdit } from '../../../../../../../types/form-types'
import { FormDelegate } from '../../../../../../../types/routing-types'
import ConfirmTransition from '../../../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../../../components/errors/messages'
import { ExternalDocumentEditForm } from '../../../../../../components/well-forms/external-document-references/external-document-edit-form'
import WellEditButtons from '../../../../../../components/wells/well-edit-buttons'
import { useGetWellData, useSaveWellData } from '../../../../../../hooks/use-api'
import useEditForm from '../../../../../../hooks/use-edit-form'
import useToastMessage from '../../../../../../hooks/use-toast-message'
import { useParamsOr404 } from '../../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import { Button, Heading, Modal, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'

export function EditExternalDocumentReference({ setIsDirty }: FormDelegate) {
  useTitleChange('Edit External document reference | External document reference')
  const { id: wellId, externalDocumentReferencesId } = useParamsOr404('id', 'externalDocumentReferencesId')
  const backUrl = `/wells/${wellId}/view/external-document-references`
  const getUrl = `/wells/${wellId}/external-document-references/${externalDocumentReferencesId}/edit`
  const method = externalDocumentReferencesId === 'new' ? HttpVerb.POST : HttpVerb.PUT
  const saveUrl =
    externalDocumentReferencesId === 'new'
      ? `wells/${wellId}/external-document-references/`
      : `/wells/${wellId}/external-document-references/${externalDocumentReferencesId}/edit`
  const viewUrl = `/wells/${wellId}/view/external-document-references/${externalDocumentReferencesId}`
  const navigate = useNavigate()

  const stateQueryResult = useGetWellData(getUrl)
  const saveMutationResult = useSaveWellData(saveUrl, method)

  const { fields, isLoading, error, isSaveError, setValue, handleSave, isSaved, validationErrors } =
    useEditForm<ExternalDocumentReferencesEdit>({
      stateQueryResult,
      saveMutationResult,
      setIsDirty,
    })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  const [isPublicWarningModalOpen, setIsPublicWarningModalOpen] = useState(false)
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null)
  const [needsPublicConfirmation, setNeedsPublicConfirmation] = useState(false)

  const handleSetValue: typeof setValue = (fieldId, value, key?, index?) => {
    if (fieldId === 'documentLinkIsPublic') {
      setNeedsPublicConfirmation(value === true)
    }
    setValue(fieldId, value, key, index)
  }

  const saveWithPublicCheck = (onSave: () => void) => {
    if (needsPublicConfirmation) {
      setPendingSave(() => onSave)
      setIsPublicWarningModalOpen(true)
    } else {
      onSave()
    }
  }

  const confirmPublicSave = () => {
    setIsPublicWarningModalOpen(false)
    setNeedsPublicConfirmation(false)
    pendingSave?.()
    setPendingSave(null)
  }

  const cancelHandler = () => {
    externalDocumentReferencesId === 'new' ? navigate(backUrl) : navigate(viewUrl)
  }

  return (
    <>
      <RecordContent>
        <Heading isSkeleton={isLoading} level={2}>
          External document reference
        </Heading>
        <ExternalDocumentEditForm
          fields={fields}
          setValue={handleSetValue}
          isLoading={isLoading}
          validationErrors={validationErrors ?? []}
          editForm={externalDocumentReferencesId !== 'new'}
        />
      </RecordContent>
      <RecordFooter areActionsFixed={externalDocumentReferencesId !== 'new'}>
        <WellEditButtons
          actions={[
            {
              label: 'Save and return',
              buttonStyle: 'primary',
              onClick: () =>
                saveWithPublicCheck(() =>
                  handleSave(() =>
                    navigate(backUrl, {
                      state: {
                        isProgrammaticTransition: true,
                      },
                    }),
                  ),
                ),
            },
            {
              label: 'Save',
              buttonStyle: 'primary',
              onClick: () =>
                saveWithPublicCheck(() =>
                  handleSave(
                    (id) =>
                      externalDocumentReferencesId === 'new' &&
                      navigate(`/wells/${wellId}/edit/external-document-references/${id}`, {
                        state: {
                          isProgrammaticTransition: true,
                        },
                      }),
                  ),
                ),
            },
            {
              label: externalDocumentReferencesId === 'new' ? 'Cancel' : 'Return to view',
              buttonStyle: 'text-action',
              onClick: () => cancelHandler(),
            },
          ]}
        />
      </RecordFooter>

      <Modal
        isOpen={isPublicWarningModalOpen}
        onRequestClose={() => setIsPublicWarningModalOpen(false)}
        heading='Make document link public?'
        body='Please review the document to confirm it contains no personal or private information before making it public. This link will be visible to anyone accessing the well record in the Documents area.'
        contentLabel='Public document link warning'
        buttons={
          <>
            <Button label='Confirm' onClick={confirmPublicSave} />
            <Button label='Cancel' buttonStyle='secondary' onClick={() => setIsPublicWarningModalOpen(false)} />
          </>
        }
      />
    </>
  )
}
export default function EditExternalDocumentReferenceWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditExternalDocumentReference setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
